import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { FieldTestService, CreateFieldTestDto, UpdateFieldTestDto } from 'src/app/core/services/field-test.service';
import { FieldTestChecklistItem } from 'src/app/core/models/field-test.model';
import { DroneService } from 'src/app/core/services/drone.service';
import { OrganizationService, OrganizationMember } from 'src/app/core/services/organization.service';
import { FieldService } from 'src/app/core/services/field.service';
import { CustomerService } from 'src/app/core/services/customer.service';
import { Drone } from 'src/app/core/models/drone.model';
import { Field } from 'src/app/core/models/field.model';
import { Customer } from 'src/app/core/models/customer.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

interface ChecklistTemplate {
  name: string;
  items: string[];
}

@Component({
  selector: 'app-field-test-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, MaxLengthDirective, CharCounterDirective],
  templateUrl: './field-test-form.component.html',
  styleUrls: ['./field-test-form.component.scss']
})
export class FieldTestFormComponent implements OnInit {
  private fieldTestService = inject(FieldTestService);
  private droneService = inject(DroneService);
  private organizationService = inject(OrganizationService);
  private fieldService = inject(FieldService);
  private customerService = inject(CustomerService);

  testForm!: FormGroup;
  isEditMode = false;
  isLoading = false;
  isSaving = false;
  testId: string | null = null;

  // Test type options
  testTypes = [
    { value: 'pre-flight', label: 'Pre-Flight Check', icon: 'icon-drone' },
    { value: 'post-flight', label: 'Post-Flight Inspection', icon: 'icon-drone-fill' },
    { value: 'equipment', label: 'Equipment Test', icon: 'bi bi-tools' },
    { value: 'calibration', label: 'Calibration', icon: 'bi bi-speedometer2' }
  ];

  // Aircraft list from DroneService
  aircraftList: { id: string; name: string }[] = [];

  // Pilot list from OrganizationService
  pilotList: { id: string; name: string }[] = [];

  // Location options from CustomerService and FieldService
  customerList: { id: string; name: string }[] = [];
  fieldList: { id: string; name: string; customerId: string | null }[] = [];
  locationOptions: { value: string; label: string }[] = [];
  showCustomLocation = false;
  customLocationValue = '';

  // Checklist templates per test type
  checklistTemplates: Record<string, ChecklistTemplate> = {
    'pre-flight': {
      name: 'Pre-Flight Checklist',
      items: [
        'Battery condition and charge level',
        'Propeller inspection for damage/wear',
        'Motor function test',
        'GPS signal acquisition',
        'Compass calibration check',
        'Remote controller connection',
        'Spray system function test',
        'Tank seal and valve inspection',
        'Landing gear inspection',
        'Firmware version check'
      ]
    },
    'post-flight': {
      name: 'Post-Flight Checklist',
      items: [
        'Visual damage inspection',
        'Propeller wear assessment',
        'Motor temperature check',
        'Battery health review',
        'Tank cleaning and drainage',
        'Nozzle cleaning',
        'Frame and arm inspection',
        'Log data download',
        'Flight hours recording'
      ]
    },
    'equipment': {
      name: 'Equipment Checklist',
      items: [
        'Power supply check',
        'Signal strength test',
        'Connection verification',
        'Software/firmware status',
        'Physical condition assessment',
        'Calibration status',
        'Accessory inspection'
      ]
    },
    'calibration': {
      name: 'Calibration Checklist',
      items: [
        'Flow rate calibration',
        'Nozzle pattern verification',
        'Pressure system test',
        'Coverage area validation',
        'Droplet size check',
        'Sensor calibration',
        'GPS accuracy verification'
      ]
    }
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.testId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.testId;

    this.initializeForm();
    this.initializePage();
  }

  /**
   * Initialize page - load lookups first, then load existing test if in edit mode
   */
  async initializePage(): Promise<void> {
    await this.loadLookups();

    if (this.isEditMode && this.testId) {
      await this.loadExistingTest();
    }
  }

  /**
   * Load all lookup data for dropdowns
   */
  async loadLookups(): Promise<void> {
    try {
      // Load aircraft (drones) from API
      await this.droneService.loadOrganizationDrones();
      const drones = this.droneService.getDrones();
      this.aircraftList = drones.map(d => ({
        id: d.id,
        name: d.name
      }));

      // Load pilots (organization members) from OrganizationService
      const members = await this.organizationService.getMyOrganizationMembers();
      this.pilotList = members.map(m => ({
        id: m.id,
        name: m.name
      }));

      // Load customers from API
      await this.customerService.loadCustomers();
      const customers = this.customerService.getCustomers();
      this.customerList = customers.map(c => ({
        id: c.id,
        name: c.name
      }));

      // Load fields from API
      await this.fieldService.loadFields();
      const fields = this.fieldService.getFieldOptions();
      this.fieldList = fields.map(f => ({
        id: f.id,
        name: f.name,
        customerId: f.customerId || null
      }));

      // Build location options: "Field Name - Customer Name" format
      this.locationOptions = this.fieldList.map(field => {
        const customer = this.customerList.find(c => c.id === field.customerId);
        const label = customer ? `${field.name} - ${customer.name}` : field.name;
        return { value: label, label };
      });
    } catch (error) {
      console.error('Error loading lookups:', error);
    }
  }

  initializeForm(): void {
    const today = new Date().toISOString().split('T')[0];

    this.testForm = this.fb.group({
      title: ['', Validators.required],
      testType: ['pre-flight', Validators.required],
      aircraft: ['', Validators.required],
      pilot: ['', Validators.required],
      date: [today, Validators.required],
      location: ['', Validators.required],
      checklist: this.fb.array([]),
      notes: ['']
    });

    // Watch for test type changes to load appropriate checklist
    this.testForm.get('testType')?.valueChanges.subscribe(type => {
      if (!this.isEditMode) {
        this.loadChecklistTemplate(type);
        this.updateTitle(type);
      }
    });

    // Load initial checklist template
    if (!this.isEditMode) {
      this.loadChecklistTemplate('pre-flight');
    }
  }

  get checklist(): FormArray {
    return this.testForm.get('checklist') as FormArray;
  }

  loadChecklistTemplate(testType: string): void {
    // Clear existing checklist
    while (this.checklist.length > 0) {
      this.checklist.removeAt(0);
    }

    // Load template items
    const template = this.checklistTemplates[testType];
    if (template) {
      template.items.forEach(item => {
        this.addChecklistItem(item);
      });
    }
  }

  addChecklistItem(itemName: string = ''): void {
    const checklistItem = this.fb.group({
      item: [itemName, Validators.required],
      passed: [false],
      notes: ['']
    });
    this.checklist.push(checklistItem);
  }

  removeChecklistItem(index: number): void {
    this.checklist.removeAt(index);
  }

  moveChecklistItem(index: number, direction: 'up' | 'down'): void {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < this.checklist.length) {
      const item = this.checklist.at(index);
      this.checklist.removeAt(index);
      this.checklist.insert(newIndex, item);
    }
  }

  updateTitle(testType: string): void {
    const aircraft = this.testForm.get('aircraft')?.value;
    const typeLabel = this.testTypes.find(t => t.value === testType)?.label || testType;

    if (aircraft) {
      const aircraftName = this.aircraftList.find(a => a.id === aircraft)?.name || '';
      this.testForm.patchValue({ title: `${typeLabel} - ${aircraftName}` });
    } else {
      this.testForm.patchValue({ title: typeLabel });
    }
  }

  onAircraftChange(): void {
    const testType = this.testForm.get('testType')?.value;
    if (testType && !this.isEditMode) {
      this.updateTitle(testType);
    }
  }

  toggleAllPassed(passed: boolean): void {
    this.checklist.controls.forEach(control => {
      control.patchValue({ passed });
    });
  }

  get passedCount(): number {
    return this.checklist.controls.filter(c => c.get('passed')?.value).length;
  }

  get failedCount(): number {
    return this.checklist.length - this.passedCount;
  }

  get overallStatus(): 'passed' | 'failed' | 'pending' {
    if (this.checklist.length === 0) return 'pending';
    if (this.passedCount === this.checklist.length) return 'passed';
    return 'failed';
  }

  async loadExistingTest(): Promise<void> {
    if (!this.testId) return;

    this.isLoading = true;

    try {
      const test = await this.fieldTestService.fetchFieldTestById(this.testId);

      if (test) {
        // Extract date from timestamp if needed
        const testDate = test.date ? test.date.split('T')[0] : '';

        // Check if location is in the dropdown options, otherwise show custom input
        const locationInOptions = this.locationOptions.some(loc => loc.value === test.location);
        if (!locationInOptions && test.location) {
          this.showCustomLocation = true;
        }

        // Populate form with test data - use IDs for dropdowns
        this.testForm.patchValue({
          title: test.title,
          testType: test.testType,
          aircraft: test.aircraftId || '', // Use ID for dropdown
          pilot: test.pilotId || '', // Use ID for dropdown
          date: testDate,
          location: test.location,
          notes: test.notes || ''
        });

        // Clear existing checklist
        while (this.checklist.length > 0) {
          this.checklist.removeAt(0);
        }

        // Load checklist items
        if (test.checklist && test.checklist.length > 0) {
          test.checklist.forEach((item: FieldTestChecklistItem) => {
            const checklistItem = this.fb.group({
              item: [item.item, Validators.required],
              passed: [item.passed],
              notes: [item.notes || '']
            });
            this.checklist.push(checklistItem);
          });
        }
      } else {
        console.error('Field test not found');
        this.router.navigate(['/logbook/testing/field']);
      }
    } catch (error) {
      console.error('Error loading field test:', error);
      this.router.navigate(['/logbook/testing/field']);
    } finally {
      this.isLoading = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.testForm.invalid) {
      this.testForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;

    try {
      const formValue = this.testForm.value;

      // Build checklist array
      const checklist: FieldTestChecklistItem[] = formValue.checklist.map((item: any) => ({
        item: item.item,
        passed: item.passed,
        notes: item.notes || ''
      }));

      // Get aircraft and pilot names from the selected values
      const aircraftName = this.aircraftList.find(a => a.id === formValue.aircraft)?.name || '';
      const pilotName = this.pilotList.find(p => p.id === formValue.pilot)?.name || '';

      if (this.isEditMode && this.testId) {
        // Update existing field test
        const updateDto: UpdateFieldTestDto = {
          title: formValue.title,
          testType: formValue.testType,
          aircraftId: formValue.aircraft,
          aircraftName: aircraftName,
          pilotId: formValue.pilot,
          pilotName: pilotName,
          testDate: formValue.date,
          location: formValue.location,
          checklist: checklist,
          notes: formValue.notes
        };

        const result = await this.fieldTestService.updateFieldTest(this.testId, updateDto);
        if (result) {
          this.router.navigate(['/logbook/testing/field']);
        } else {
          console.error('Failed to update field test');
        }
      } else {
        // Create new field test
        const createDto: CreateFieldTestDto = {
          title: formValue.title,
          testType: formValue.testType,
          aircraftId: formValue.aircraft,
          aircraftName: aircraftName,
          pilotId: formValue.pilot,
          pilotName: pilotName,
          testDate: formValue.date,
          location: formValue.location,
          checklist: checklist,
          notes: formValue.notes
        };

        const result = await this.fieldTestService.createFieldTest(createDto);
        if (result) {
          this.router.navigate(['/logbook/testing/field']);
        } else {
          console.error('Failed to create field test');
        }
      }
    } catch (error) {
      console.error('Error saving field test:', error);
    } finally {
      this.isSaving = false;
    }
  }

  onCancel(): void {
    this.router.navigate(['/logbook/testing/field']);
  }

  getTestTypeIcon(type: string): string {
    return this.testTypes.find(t => t.value === type)?.icon || 'bi bi-clipboard-check';
  }

  getTestTypeLabel(): string {
    const type = this.testForm.get('testType')?.value;
    return this.testTypes.find(t => t.value === type)?.label || '';
  }

  getAircraftName(): string {
    const id = this.testForm.get('aircraft')?.value;
    return this.aircraftList.find(a => a.id === id)?.name || '';
  }

  getPilotName(): string {
    const id = this.testForm.get('pilot')?.value;
    return this.pilotList.find(p => p.id === id)?.name || '';
  }

  /**
   * Handle location dropdown change
   */
  onLocationChange(): void {
    const value = this.testForm.get('location')?.value;
    if (value === '__other__') {
      this.showCustomLocation = true;
      this.testForm.patchValue({ location: '' });
    } else {
      this.showCustomLocation = false;
    }
  }

  /**
   * Cancel custom location and go back to dropdown
   */
  cancelCustomLocation(): void {
    this.showCustomLocation = false;
    this.customLocationValue = '';
    this.testForm.patchValue({ location: '' });
  }
}
